// =============================================================================
// Data Room Access Modal
// =============================================================================
// Modal for granting data room access to contacts
// =============================================================================

import { useEffect, useState } from 'react';
import { XMarkIcon, BuildingOfficeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError } from '../../../services/api';
import type { AvailableDeal, ContactDataRoomAccess } from '../../../types/fundraising';
import { DEAL_STATUS_LABELS } from '../../../types/fundraising';

// =============================================================================
// Types
// =============================================================================

interface DataRoomAccessModalProps {
  isOpen: boolean;
  contactId: string;
  contactName: string;
  existingAccess: ContactDataRoomAccess[];
  onClose: () => void;
  onAccessGranted: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function DataRoomAccessModal({
  isOpen,
  contactId,
  contactName,
  existingAccess,
  onClose,
  onAccessGranted,
}: DataRoomAccessModalProps) {
  const [availableDeals, setAvailableDeals] = useState<AvailableDeal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [ndaRequired, setNdaRequired] = useState(true);
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load available deals when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableDeals();
      setSelectedDealId('');
      setNdaRequired(true);
      setExpiresAt('');
      setError(null);
    }
  }, [isOpen]);

  const loadAvailableDeals = async () => {
    setIsLoading(true);
    try {
      const deals = await fundraisingService.getAvailableDeals();
      setAvailableDeals(deals);
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDealId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await fundraisingService.grantDataRoomAccess({
        contactId,
        dealId: selectedDealId,
        ndaRequired,
        expiresAt: expiresAt || null,
      });
      onAccessGranted();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter out deals that already have active access
  const activeAccessDealIds = existingAccess
    .filter((a) => !a.revokedAt && (!a.expiresAt || new Date(a.expiresAt) > new Date()))
    .map((a) => a.dealId);

  const dealsWithoutAccess = availableDeals.filter(
    (deal) => !activeAccessDealIds.includes(deal.id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Grant Data Room Access</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Grant <span className="font-medium text-gray-900">{contactName}</span> access to a deal's
          data room.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading deals...</p>
          </div>
        ) : dealsWithoutAccess.length === 0 ? (
          <div className="text-center py-8">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">
              {availableDeals.length === 0
                ? 'No deals available for data room access.'
                : 'This contact already has access to all available deals.'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Deal Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Deal *</label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {dealsWithoutAccess.map((deal) => (
                  <button
                    key={deal.id}
                    type="button"
                    onClick={() => setSelectedDealId(deal.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors text-left ${
                      selectedDealId === deal.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{deal.name}</p>
                        <p className="text-xs text-gray-500">{DEAL_STATUS_LABELS[deal.status] || deal.status}</p>
                      </div>
                    </div>
                    {selectedDealId === deal.id && (
                      <CheckIcon className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* NDA Required */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ndaRequired"
                checked={ndaRequired}
                onChange={(e) => setNdaRequired(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="ndaRequired" className="text-sm text-gray-700">
                Require NDA before accessing documents
              </label>
            </div>

            {/* Expiration Date */}
            <div>
              <label htmlFor="expiresAt" className="block text-sm font-medium text-gray-700 mb-1">
                Access Expiration (optional)
              </label>
              <input
                type="date"
                id="expiresAt"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank for no expiration</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting || !selectedDealId}
              >
                {isSubmitting ? 'Granting...' : 'Grant Access'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default DataRoomAccessModal;
